import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { api } from "../../api";
import usePageTitle from "../../hooks/usePageTitle";
import AppShell from "../AppShell";
import ImageWithFallback from "../ImageWithFallback";
import { buildItemUrl } from "../../utils/itemUrl";

const FILTERS = [
  { value: "all", label: "All" },
  { value: "cards", label: "Cards" },
  { value: "jerseys", label: "Jerseys" },
  { value: "signed", label: "Signed" },
  { value: "funko", label: "Funko" },
  { value: "lego", label: "LEGO" },
];

const panelSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#ffffff",
  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
};

const interactiveCardSx = {
  borderRadius: 2,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#f9fbfd",
  transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease",
  cursor: "pointer",
  textDecoration: "none",
  color: "inherit",
  display: "block",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 16px 30px rgba(15, 23, 42, 0.12)",
    borderColor: "rgba(18, 89, 156, 0.20)",
    backgroundColor: "#ffffff",
  },
};

function normalizeText(value) {
  return String(value || "").toLowerCase().trim();
}

function isSignedItem(item) {
  const value = item?.is_signed;
  return value === 1 || value === true || value === "1";
}

function getPrimaryImage(item) {
  return item?.image_url || item?.ref_image_url || "";
}

function isLegoItem(item) {
  const category = normalizeText(item?.category);
  const brand = normalizeText(item?.brand);
  const itemType = normalizeText(item?.item_type);
  const name = normalizeText(item?.name);
  const setName = normalizeText(item?.set_name);

  return (
    category.includes("lego") ||
    brand.includes("lego") ||
    itemType.includes("lego") ||
    name.includes("lego") ||
    setName.includes("lego")
  );
}

function isFunkoItem(item) {
  const category = normalizeText(item?.category);
  const brand = normalizeText(item?.brand);
  const name = normalizeText(item?.name);

  return (
    category.includes("funko") ||
    brand.includes("funko") ||
    name.includes("funko")
  );
}

function matchesFilter(item, filter) {
  const category = normalizeText(item?.category);
  const itemType = normalizeText(item?.item_type);
  const isLego = isLegoItem(item);

  switch (filter) {
    case "cards":
      return category === "sports card" && !isLego;
    case "jerseys":
      return itemType.includes("jersey") && !isSignedItem(item) && !isLego;
    case "signed":
      return isSignedItem(item) && !isLego;
    case "funko":
      return isFunkoItem(item) && !isLego;
    case "lego":
      return isLego;
    case "all":
    default:
      return true;
  }
}

function formatMetaLine(item) {
  const parts = [
    item?.year_released,
    item?.brand,
    item?.set_name,
    item?.player,
    item?.team,
  ].filter(Boolean);

  return parts.join(" | ");
}

function formatDetailLine(item) {
  const details = [
    item?.item_type,
    item?.number ? `#${item.number}` : null,
    item?.rarity,
    item?.edition_variant,
    isSignedItem(item) ? "Signed" : null,
    item?.authentication,
  ].filter(Boolean);

  return details.join(" | ");
}

function SectionBlock({ title, subtitle, children, sx }) {
  return (
    <Paper elevation={0} sx={{ ...panelSx, ...sx }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: "primary.main", mb: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 820 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {children}
    </Paper>
  );
}

function PlaceholderImage() {
  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        minHeight: 220,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "rgba(12, 35, 64, 0.08)",
        backgroundColor: "#eef3f8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "text.secondary",
        fontWeight: 700,
        letterSpacing: 0.3,
      }}
    >
      No Image
    </Box>
  );
}

function ItemCard({ item, featured = false }) {
  const imageSrc = getPrimaryImage(item);
  const itemUrl = buildItemUrl(item);
  const metaLine = formatMetaLine(item);
  const detailLine = formatDetailLine(item);

  return (
    <Box
      component={RouterLink}
      to={itemUrl}
      sx={{
        ...interactiveCardSx,
        p: featured ? { xs: 1.25, md: 1.5 } : { xs: 1.25, md: 1.5 },
        height: "100%",
      }}
    >
      <Box
        sx={{
          height: featured ? 140 : 145,
          borderRadius: 2,
          overflow: "hidden",
          mb: featured ? 1.1 : 1.1,
          backgroundColor: "#eef3f8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {imageSrc ? (
          <ImageWithFallback
            src={item.image_url}
            fallbackSrcs={[item.ref_image_url]}
            alt={item.name || "Event item"}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <PlaceholderImage />
        )}
      </Box>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
        {featured && (
          <Chip
            label="Featured"
            size="small"
            sx={{
              fontWeight: 700,
              backgroundColor: "rgba(18, 89, 156, 0.10)",
              color: "primary.main",
            }}
          />
        )}
        {item?.category && (
          <Chip
            label={item.category}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 700, backgroundColor: "#ffffff" }}
          />
        )}
        {isSignedItem(item) && (
          <Chip
            label="Signed"
            size="small"
            color="success"
            sx={{ fontWeight: 700 }}
          />
        )}
      </Stack>

      <Typography
        variant={featured ? "h6" : "subtitle1"}
        sx={{
          fontWeight: 800,
          color: "#10233f",
          lineHeight: 1.25,
          mb: featured ? 0.55 : 0.55,
        }}
      >
        {item?.name || `Item #${item?.item_id}`}
      </Typography>

      {metaLine && (
        <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55, mb: 0.5 }}>
          {metaLine}
        </Typography>
      )}

      {detailLine && (
        <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55 }}>
          {detailLine}
        </Typography>
      )}
    </Box>
  );
}

export default function RoadToWorldCupPage() {
  usePageTitle("Road to World Cup");
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadItems = async () => {
      try {
        setLoading(true);
        setError("");
        const { data } = await api.get("/api/events/road-to-world-cup/items");
        if (!mounted) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.error || err?.message || "Failed to load event items.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadItems();
    return () => {
      mounted = false;
    };
  }, []);

  const featuredItems = useMemo(
    () => items.filter((item) => Number(item?.is_featured) === 1),
    [items]
  );

  const filteredItems = useMemo(
    () => items.filter((item) => matchesFilter(item, filter)),
    [items, filter]
  );

  const filterCounts = useMemo(() => {
    return FILTERS.reduce((acc, entry) => {
      acc[entry.value] = items.filter((item) => matchesFilter(item, entry.value)).length;
      return acc;
    }, {});
  }, [items]);

  const topCardsItems = useMemo(
    () => filteredItems.filter((item) => normalizeText(item?.category) === "sports card" && !isLegoItem(item)),
    [filteredItems]
  );

  const jerseysItems = useMemo(
    () =>
      filteredItems.filter(
        (item) => normalizeText(item?.item_type).includes("jersey") && !isSignedItem(item) && !isLegoItem(item)
      ),
    [filteredItems]
  );

  const signedMemorabiliaItems = useMemo(
    () => filteredItems.filter((item) => isSignedItem(item) && !isLegoItem(item)),
    [filteredItems]
  );

  const funkoItems = useMemo(
    () => filteredItems.filter((item) => isFunkoItem(item) && !isLegoItem(item)),
    [filteredItems]
  );

  const legoItems = useMemo(
    () => filteredItems.filter((item) => isLegoItem(item)),
    [filteredItems]
  );

  const sectionConfigs = [
    { key: "top-cards", title: "Top Cards", items: topCardsItems },
    { key: "jerseys", title: "Jerseys", items: jerseysItems },
    { key: "signed-memorabilia", title: "Signed Memorabilia", items: signedMemorabiliaItems },
    { key: "funko", title: "Funko", items: funkoItems },
    { key: "world-cup-lego", title: "World Cup LEGO", items: legoItems },
  ];

  return (
    <AppShell>
      <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 1, md: 1.5 } }}>
        <Stack spacing={{ xs: 3.5, md: 6.5 }}>
          <Paper
            elevation={0}
            sx={{
              ...panelSx,
              p: { xs: 2.5, md: 3.5 },
              position: "relative",
              overflow: "hidden",
              background: "linear-gradient(135deg, #13233a 0%, #1f3a5f 100%)",
              borderColor: "rgba(255,255,255,0.08)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(circle at top right, rgba(255,255,255,0.12), transparent 34%), radial-gradient(circle at bottom left, rgba(255,255,255,0.08), transparent 28%)",
                pointerEvents: "none",
              }}
            />
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  color: "rgba(255,255,255,0.68)",
                  fontWeight: 800,
                  letterSpacing: 1.2,
                  mb: 1,
                }}
              >
                Trending Event
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  color: "#ffffff",
                  fontWeight: 900,
                  lineHeight: 1.08,
                  mb: 1.25,
                  maxWidth: 760,
                  fontSize: { xs: "2rem", md: "2.85rem" },
                }}
              >
                Road to World Cup
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.76)",
                  maxWidth: 780,
                  lineHeight: 1.65,
                  fontSize: { xs: "0.98rem", md: "1.04rem" },
                  mb: 2,
                }}
              >
                Global stars, breakout rookies, signed memorabilia, World Cup LEGO, and tournament-driven storylines are pulling soccer collectors toward the cards and collectibles with the strongest World Cup momentum.
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {["Soccer", "Global Event", "World Cup LEGO", "World Cup Watch"].map((chip) => (
                  <Chip
                    key={chip}
                    label={chip}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      backgroundColor: "rgba(255,255,255,0.10)",
                      color: "#ffffff",
                      border: "1px solid rgba(255,255,255,0.14)",
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Paper>

          {loading ? (
            <Paper elevation={0} sx={{ ...panelSx, py: { xs: 5, md: 7 } }}>
              <Stack spacing={1.5} alignItems="center" justifyContent="center">
                <CircularProgress size={34} />
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  Loading World Cup items...
                </Typography>
              </Stack>
            </Paper>
          ) : error ? (
            <Paper elevation={0} sx={panelSx}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            </Paper>
          ) : (
            <>
              {featuredItems.length > 0 && (
                <SectionBlock
                  title="Featured Picks"
                  subtitle="Highlighted items already mapped to this event and surfaced from the featured entries in the database."
                  sx={{ mt: { xs: 1, md: 1.5 }, mb: { xs: 0.5, md: 1 } }}
                >
                  <Grid container spacing={1.5}>
                    {featuredItems.map((item) => (
                      <Grid item xs={6} sm={4} md={3} key={item.item_id}>
                        <ItemCard item={item} featured />
                      </Grid>
                    ))}
                  </Grid>
                </SectionBlock>
              )}

              <SectionBlock
                title="Event Collection"
                subtitle={`${items.length} mapped items tied to Road to World Cup.`}
              >
                <Stack spacing={2}>
                  <ToggleButtonGroup
                    value={filter}
                    exclusive
                    onChange={(_event, nextValue) => {
                      if (nextValue) setFilter(nextValue);
                    }}
                    sx={{
                      flexWrap: "wrap",
                      gap: 1,
                      "& .MuiToggleButtonGroup-grouped": {
                        borderRadius: "999px !important",
                        border: "1px solid rgba(12, 35, 64, 0.12) !important",
                        px: 1.5,
                        py: 0.7,
                        textTransform: "none",
                        fontWeight: 700,
                        color: "#10233f",
                      },
                    }}
                  >
                    {FILTERS.map((entry) => (
                      <ToggleButton key={entry.value} value={entry.value}>
                        {entry.label} ({filterCounts[entry.value] || 0})
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>

                  {items.length === 0 ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        borderColor: "rgba(12, 35, 64, 0.08)",
                        backgroundColor: "#f9fbfd",
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#10233f", mb: 0.5 }}>
                        No event items found
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        There are currently no items mapped to this event.
                      </Typography>
                    </Paper>
                  ) : filteredItems.length === 0 ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        borderColor: "rgba(12, 35, 64, 0.08)",
                        backgroundColor: "#f9fbfd",
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#10233f", mb: 0.5 }}>
                        No items match this filter
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Try another view to see the rest of the event collection.
                      </Typography>
                    </Paper>
                  ) : (
                    <Stack spacing={{ xs: 4, md: 6 }}>
                      {sectionConfigs.map((section) => {
                        if (section.items.length === 0) return null;

                        return (
                          <Box key={section.key}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: 800, mb: 2, color: "#10233f" }}
                            >
                              {section.title}
                            </Typography>
                            <Grid container spacing={1.5}>
                              {section.items.map((item) => (
                                <Grid item xs={6} sm={4} md={3} key={item.item_id}>
                                  <ItemCard item={item} />
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Stack>
              </SectionBlock>
            </>
          )}
        </Stack>
      </Box>
    </AppShell>
  );
}
